import { generatePath, useNavigate } from "../../router-utils"
import { TFileData, transportServicesPriceXHR } from "./store"
import { transportServicesXHR as XHR } from "../../TransportServices/store"
import {
	AsyncSelect,
	AsyncSelectProps,
	Box,
	Inline,
	joinAttributes,
	Money,
	Spinner,
	Stack,
	Table,
	TableDataCell,
	TableHeaderDataCell,
	Text,
	Time,
} from "@sembark-travel/ui/base"
import { queryToSearch } from "@sembark-travel/ui/router"
import { TSeason } from "./store"
import { useXHR } from "@sembark-travel/xhr"
import React, { Fragment, useMemo, useRef, useState } from "react"

import {
	AddTransportServiceDialog,
	ITransportService,
} from "../../TransportServices"

import { compareTransportFiles } from "./utils"
import { INewServiceCredentials } from "./NewItem"
import {
	CompareSheetFileVersions,
	ReadSheetFileVersionDetails,
} from "../Sheets"
import { TTripDestination } from "../../TripDestinations"

export function TSPFileVersionDetails({
	folderId,
	fileId,
	versionId,
}: {
	folderId: string
	fileId: string
	versionId: string
}) {
	return (
		<ReadSheetFileVersionDetails<TFileData, TFileData>
			sheetFolderId={folderId}
			sheetId={fileId}
			sheetVersionId={versionId}
			getLinkToEdit={(folderId, fileId, versionId) => {
				return generatePath(
					"/data-drive/folders/:folderId/files/:fileId/versions/:versionId/edit/transport",
					{
						folderId: folderId,
						fileId: fileId,
						versionId: versionId,
					}
				)
			}}
			service={{
				fetchSheetVersionDetails: (xhr, versionId) => {
					return transportServicesPriceXHR(xhr).getFileVersionDetails(versionId)
				},
			}}
			transformDataToFormValues={(sheetValues) => sheetValues}
		>
			{({ sheetValues }) => {
				return sheetValues ? <ViewFiles fileDetails={sheetValues} /> : null
			}}
		</ReadSheetFileVersionDetails>
	)
}

export function TSPCompareFiles({
	fileId,
	versionId1,
	versionId2,
}: {
	fileId: string
	versionId1: string
	versionId2: string
}) {
	const navigate = useNavigate()
	return (
		<CompareSheetFileVersions<TFileData, TFileData>
			sheetId={fileId}
			sheetVersionId1={versionId1}
			sheetVersionId2={versionId2}
			xhrFetchBaseUrl={`/data-drive`}
			service={{
				fetchSheetDetails: (xhr, fileId) => {
					return transportServicesPriceXHR(xhr).getFileDetails(fileId)
				},
				fetchSheetVersionDetails: (xhr, versionId) => {
					return transportServicesPriceXHR(xhr).getFileVersionDetails(versionId)
				},
			}}
			transformDataToFormValues={(sheetValues) => sheetValues}
			onCompareWithSheetChange={(
				folderId,
				folderName,
				fileId,
				fileName,
				versionId1,
				versionId2
			) => {
				navigate(
					{
						pathname:
							"/data-drive/folders/:folderId/files/:fileId/compare/:versionId1/:versionId2/transport",
						search: queryToSearch({
							folder: folderName,
							file: fileName,
						}),
					},
					{
						params: {
							folderId: folderId,
							fileId: fileId,
							versionId1: versionId1,
							versionId2: versionId2,
						},
					}
				)
			}}
		>
			{({ sheet1Values, sheet2Values }) => {
				return sheet1Values && sheet2Values ? (
					<TSPVersionDiff
						sheet1Values={sheet1Values}
						sheet2Values={sheet2Values}
					/>
				) : (
					<Spinner alignCenter padding="4" />
				)
			}}
		</CompareSheetFileVersions>
	)
}

export function ViewFiles({ fileDetails }: { fileDetails: TFileData }) {
	const serviceSeasons: Array<TSeason> | undefined = fileDetails.seasons
	const rowSpan = serviceSeasons[0]?.intervals.length + 2 || 2
	const fileData = fileDetails.prices
	return (
		<Box paddingTop={"4"}>
			<Stack gap="2">
				<Inline gap="1">
					<Box
						backgroundColor={"default"}
						padding={"2"}
						borderWidth={"1"}
						borderColor={"muted"}
						style={{
							maxWidth: "140px",
						}}
					>
						<Text fontSize={"sm"}>Currency: {fileDetails.currency}</Text>
					</Box>
					<Box
						backgroundColor={"default"}
						padding={"2"}
						borderWidth={"1"}
						borderColor={"muted"}
					>
						<Text fontSize={"sm"}>
							Destinations:{" "}
							{fileDetails.trip_destinations.map((d) => d.name).join(", ")}
						</Text>
					</Box>
				</Inline>

				<Box>
					<Table
						aria-label="CSV file format for transport service prices"
						responsive
						className="excel-style-table price-table"
						style={{
							width: "100%",
							maxHeight: "100vh",
							overflowY: "auto",
						}}
					>
						<thead
							style={{
								position: "sticky",
								top: 0,
								zIndex: 0,
							}}
						>
							<tr>
								<TableHeaderDataCell
									rowSpan={rowSpan}
									bgColor={"default"}
									style={{
										position: "sticky",
										left: 0,
										minWidth: "200px",
										zIndex: 0,
									}}
								>
									Transport Service Location
								</TableHeaderDataCell>
								<TableHeaderDataCell
									rowSpan={rowSpan}
									bgColor={"default"}
									style={{
										position: "sticky",
										minWidth: "200px",
										left: 200,
										zIndex: -1,
									}}
								>
									Transport Service Type
								</TableHeaderDataCell>
								{serviceSeasons?.map((season, index) => {
									const colSpan = season.cab_types?.length
									return (
										<TableHeaderDataCell
											colSpan={colSpan}
											bgColor={"default"}
											key={index}
											style={{ position: "sticky", zIndex: -2 }}
										>
											{season.name}
										</TableHeaderDataCell>
									)
								})}
							</tr>
							<tr>
								{serviceSeasons?.map((season, index) => {
									const colSpan = season.cab_types?.length
									return (
										<TableHeaderDataCell
											colSpan={colSpan}
											bgColor={"default"}
											key={index}
											style={{ position: "sticky", zIndex: -2 }}
										>
											{season.intervals?.map(
												(dateRange, index) =>
													dateRange?.start_date &&
													dateRange?.end_date && (
														<Inline key={index}>
															<Time timestamp={dateRange?.start_date} /> {" - "}
															<Time timestamp={dateRange?.end_date} />
														</Inline>
													)
											)}
										</TableHeaderDataCell>
									)
								})}
							</tr>
							<tr>
								{serviceSeasons?.map((season) =>
									season.cab_types?.map((cabType, index) => (
										<TableHeaderDataCell
											bgColor={"default"}
											key={index}
											style={{ position: "sticky", zIndex: -2 }}
										>
											{cabType.name}
										</TableHeaderDataCell>
									))
								)}
							</tr>
						</thead>
						<tbody>
							{(fileData || []).map(
								(
									{
										transport_service_location: transport_service_location_id,
										transport_services,
									},
									index
								) => {
									const rowSpan = transport_services.length
									return (
										<React.Fragment key={index}>
											<tr key={index}>
												<TableDataCell
													rowSpan={rowSpan + 1}
													bgColor={"default"}
													style={{
														position: "sticky",
														left: 0,
														minWidth: "200px",
														zIndex: -1,
													}}
												>
													<Text fontWeight="semibold">
														{transport_service_location_id?.name}
													</Text>
												</TableDataCell>
											</tr>
											{transport_services.map((service, index) => {
												return (
													<tr key={index}>
														<TableDataCell
															bgColor={"default"}
															style={{
																position: "sticky",
																left: 200,
																minWidth: "200px",
																zIndex: -2,
															}}
														>
															<Stack gap="1">
																<Text fontWeight="semibold">
																	{service?.transport_service.service}
																</Text>
																<Box
																	fontSize="sm"
																	fontWeight="semibold"
																	color="muted"
																>
																	{joinAttributes(
																		service?.transport_service.distance
																			? `${service?.transport_service.distance} kms`
																			: null,
																		service?.transport_service
																			.start_time_formatted
																			? `Starts: ${service?.transport_service.start_time_formatted}`
																			: null,
																		service?.transport_service
																			.duration_string || null,
																		service?.transport_service
																			.end_time_formatted
																			? `Ends: ${service?.transport_service.end_time_formatted}`
																			: null
																	)}
																</Box>
															</Stack>
														</TableDataCell>
														{serviceSeasons.map((season, index) =>
															season.cab_types.map(({ id }) => {
																const money =
																	service?.seasons?.[season.id]?.prices?.[id]
																		?.prices
																return (
																	<td
																		key={id}
																		style={{
																			position: "relative",
																			zIndex: -3,
																		}}
																	>
																		{money ? <Money money={money} /> : "---"}
																	</td>
																)
															})
														)}
													</tr>
												)
											})}
										</React.Fragment>
									)
								}
							)}
						</tbody>
					</Table>
				</Box>
			</Stack>
		</Box>
	)
}

export function TSPVersionDiff({
	sheet1Values,
	sheet2Values,
}: {
	sheet1Values: TFileData
	sheet2Values: TFileData
}) {
	const diff = useMemo(() => {
		// deep clone the values to avoid mutating the original values
		const latestVersion = structuredClone(sheet1Values)
		const oldVersion = structuredClone(sheet2Values)
		const diff = compareTransportFiles(latestVersion, oldVersion)
		return diff
	}, [sheet1Values, sheet2Values])
	const latestVersionSeason: Array<TSeason> | undefined = diff.seasons
	return (
		<Box borderRadius="md" boxShadow={"lg"} bgColor="default">
			<Stack gap="4">
				<Box>
					<Table
						aria-label="CSV file format for transport service prices"
						responsive
						className="excel-style-table price-table"
						style={{
							width: "100%",
							maxHeight: "100vh",
							overflowY: "auto",
						}}
					>
						<thead
							style={{
								position: "sticky",
								top: 0,
								zIndex: 0,
							}}
						>
							<tr>
								<TableHeaderDataCell
									rowSpan={3}
									bgColor={"default"}
									style={{
										position: "sticky",
										left: 0,
										minWidth: "200px",
										zIndex: 0,
									}}
								>
									Transport Service Location
								</TableHeaderDataCell>
								<TableHeaderDataCell
									rowSpan={3}
									bgColor={"default"}
									style={{
										position: "sticky",
										minWidth: "200px",
										left: 200,
										zIndex: -1,
									}}
								>
									Transport Service Type
								</TableHeaderDataCell>
								{latestVersionSeason?.map((season, index) => {
									const colSpan = season.cab_types?.length
									return (
										<TableHeaderDataCell
											colSpan={colSpan}
											bgColor={"default"}
											key={index}
											style={{ position: "sticky", zIndex: -2 }}
										>
											{season.name}
										</TableHeaderDataCell>
									)
								})}
							</tr>
							<tr>
								{latestVersionSeason?.map((season, index) => {
									const colSpan = season.cab_types?.length
									return (
										<TableHeaderDataCell
											colSpan={colSpan}
											bgColor={"default"}
											key={index}
											style={{ position: "sticky", zIndex: -2 }}
										>
											{season.intervals?.map(
												(dateRange, index) =>
													dateRange?.start_date &&
													dateRange?.end_date && (
														<Inline key={index}>
															<Time timestamp={dateRange?.start_date} /> {" - "}
															<Time timestamp={dateRange?.end_date} />
														</Inline>
													)
											)}
										</TableHeaderDataCell>
									)
								})}
							</tr>
							<tr>
								{latestVersionSeason?.map((season) =>
									season.cab_types?.map((cabType, index) => {
										return (
											<TableHeaderDataCell
												key={index}
												bgColor={"default"}
												style={{ position: "sticky", zIndex: -2 }}
											>
												{cabType.name}
											</TableHeaderDataCell>
										)
									})
								)}
							</tr>
						</thead>
						<tbody>
							{diff.prices?.map(
								(
									{ transport_service_location, transport_services, status },
									index
								) => {
									const rowSpan = transport_services.length
									const col = status
										? status === "added"
											? "success"
											: "danger"
										: "default"
									return (
										<React.Fragment key={index}>
											<tr key={index}>
												<TableDataCell
													bgColor={col}
													rowSpan={rowSpan + 1}
													style={{
														position: "sticky",
														left: 0,
														minWidth: "200px",
														zIndex: -1,
													}}
												>
													<Text fontWeight="semibold">
														{transport_service_location?.name}
													</Text>
												</TableDataCell>
											</tr>
											{transport_services.map((service, index) => {
												const serviceCol = service?.status
													? service?.status === "added"
														? "success_emphasis"
														: "danger_emphasis"
													: col
												return (
													<tr key={index}>
														<TableDataCell
															bgColor={serviceCol}
															style={{
																position: "sticky",
																left: 200,
																minWidth: "200px",
																zIndex: -2,
															}}
														>
															<Stack gap="1">
																<Text fontWeight="semibold">
																	{service?.transport_service.service}
																</Text>
																<Box
																	fontSize="sm"
																	fontWeight="semibold"
																	color="muted"
																>
																	{joinAttributes(
																		service?.transport_service.distance
																			? `${service?.transport_service.distance} kms`
																			: null,
																		service?.transport_service
																			.start_time_formatted
																			? `Starts: ${service?.transport_service.start_time_formatted}`
																			: null,
																		service?.transport_service
																			.duration_string || null,
																		service?.transport_service
																			.end_time_formatted
																			? `Ends: ${service?.transport_service.end_time_formatted}`
																			: null
																	)}
																</Box>
															</Stack>
														</TableDataCell>
														{latestVersionSeason.map((season, index) =>
															season.cab_types.map(({ id }) => {
																const money =
																	service?.seasons?.[season.id]?.prices?.[id]
																		?.prices
																const oldMoney =
																	service?.seasons?.[season.id]?.prices?.[id]
																		?.prices?.oldValue

																const seasonStatus =
																	service?.seasons?.[season.id]?.status
																const col = seasonStatus
																	? seasonStatus === "modified"
																		? "warning_emphasis"
																		: seasonStatus === "added"
																			? "success_emphasis"
																			: "danger_emphasis"
																	: serviceCol
																const priceStatus =
																	service?.seasons?.[season.id]?.prices?.[id]
																		?.prices?.status
																const priceCol = priceStatus
																	? priceStatus === "modified"
																		? "warning_emphasis"
																		: priceStatus === "added"
																			? "success_emphasis"
																			: "danger_emphasis"
																	: col
																return (
																	<TableDataCell
																		key={id}
																		bgColor={priceCol}
																		style={{
																			position: "relative",
																			zIndex: -3,
																		}}
																	>
																		<Text>
																			{oldMoney ? (
																				<span
																					style={{
																						textDecoration: "line-through",
																						paddingRight: "1rem",
																					}}
																				>
																					<Money money={oldMoney} />
																				</span>
																			) : null}
																			{money ? <Money money={money} /> : null}
																		</Text>
																	</TableDataCell>
																)
															})
														)}
													</tr>
												)
											})}
										</React.Fragment>
									)
								}
							)}
						</tbody>
					</Table>
				</Box>
			</Stack>
		</Box>
	)
}

export function SelectTransportServices({
	tripDestinations,
	initialValues,
	...props
}: Omit<AsyncSelectProps, "fetch"> & {
	tripDestinations?: Array<TTripDestination>
	initialValues?: INewServiceCredentials
}) {
	const xhr = useXHR()
	const [newName, setNewName] = useState("")
	const promiseOfCreation = useRef<
		((transportService: ITransportService) => void) | undefined
	>(undefined)

	return (
		<Fragment>
			<AsyncSelect
				style={{
					position: "relative",
				}}
				createOptionLabel={(q) => `Add "${q}"`}
				multiple
				optionRenderer={({
					option,
					created,
				}: {
					option: ITransportService
					created?: boolean
				}) =>
					created ? (
						<Box>Add "{option.name}"</Box>
					) : (
						<Box>
							<Box fontSize="sm" style={{ zIndex: 900 }}>
								{option.name}
							</Box>
						</Box>
					)
				}
				onCreateNew={(query: string) => {
					setNewName(query)
					return new Promise((resolve) => {
						promiseOfCreation.current = resolve
					})
				}}
				{...props}
				fetch={(q) =>
					XHR(xhr)
						.getTransportServices({
							q,
							trip_destinations: tripDestinations?.length
								? tripDestinations.map((t) => t.id)
								: null,
						})
						.then((resp) =>
							resp.data.map((data) => ({ ...data, disable: true }))
						)
				}
			/>
			<AddTransportServiceDialog
				open={!!newName}
				onClose={() => {
					setNewName("")
				}}
				initialValues={{
					...initialValues,
					trip_destinations: tripDestinations,
				}}
				onSuccess={(transportService) => {
					promiseOfCreation.current?.(transportService)
				}}
				type="addLocationService"
				serviceLocationId={undefined}
				canAddMoreServices={false}
				url={`/transport-services`}
			/>
		</Fragment>
	)
}
